import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/authService';
import { InvitationInfo, AuthUser } from '@/types';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface LoginForm {
   email: string;
   password: string;
}

interface PasswordChangeForm {
   newPassword: string;
   confirmPassword: string;
}

export const LoginPage = () => {
   const { login } = useAuth();
   const navigate = useNavigate();
   const [isLoading, setIsLoading] = useState(false);
   const [invitationToken, setInvitationToken] = useState<string | null>(null);
   const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
   const [isLoadingInvitation, setIsLoadingInvitation] = useState(false);
   const [showPasswordChange, setShowPasswordChange] = useState(false);
   const [loggedInUser, setLoggedInUser] = useState<AuthUser | null>(null);
   const [tempPassword, setTempPassword] = useState<string>('');

  const {
     register,
     handleSubmit,
     setValue,
     formState: { errors },
  } = useForm<LoginForm>();

   const {
      register: registerPassword,
      handleSubmit: handlePasswordSubmit,
      formState: { errors: passwordErrors },
      watch,
   } = useForm<PasswordChangeForm>();

   const newPassword = watch('newPassword');

   // Check for invitation token in URL on component mount
   useEffect(() => {
      const checkInvitation = async () => {
         const token = authService.parseInvitationFromUrl();
         if (token) {
            setInvitationToken(token);
            setIsLoadingInvitation(true);

            try {
               const inviteInfo = await authService.verifyInvitation(token);
               setInvitationInfo(inviteInfo);
               // Pre-fill email from invitation
               setValue('email', inviteInfo.email);
            } catch (error: any) {
               const errorMessage = error.response?.data?.error || 'Invalid or expired invitation';
               toast.error(errorMessage);
               // Clear invitation token from URL
               window.history.replaceState({}, document.title, window.location.pathname);
            } finally {
               setIsLoadingInvitation(false);
            }
         }
      };

      checkInvitation();
   }, [setValue]);

  const onSubmit = async (data: LoginForm) => {
    try {
       setIsLoading(true);

       const user = await login({
          email: data.email,
          password: data.password,
          invitationToken: invitationToken || undefined,
       });

       console.log('🔐 Login successful, user data:', {
          email: user.email,
          isFirstLogin: user.isFirstLogin,
          requiresPasswordChange: user.requiresPasswordChange,
          hasInvitationToken: !!invitationToken,
       });

       if (invitationToken && user.requiresPasswordChange) {
          // For invitation login, show password change form instead of redirecting
          console.log('🔄 Showing password change form for invitation login');
          setLoggedInUser(user);
          setTempPassword(data.password);
          setShowPasswordChange(true);
          toast.success('Welcome! Please set your new password to complete setup.');
          // Clear invitation token from URL
          window.history.replaceState({}, document.title, '/');
       } else if (user.requiresPasswordChange) {
          console.log('🔄 Redirecting to password change page');
          navigate('/change-password');
       } else {
          console.log('🏠 Redirecting to dashboard');
          toast.success('Login successful!');
          navigate('/');
       }
    } catch (error: any) {
       const errorCode = error.response?.data?.code;
       const errorMessage = error.response?.data?.error || error.message || 'Login failed';

       if (errorCode === 'PASSWORD_CHANGE_REQUIRED') {
          toast.error(
             'You must change your password before logging in. Please use your invitation link.'
          );
       } else {
          toast.error(errorMessage);
       }
    } finally {
       setIsLoading(false);
    }
  };

   const onPasswordChangeSubmit = async (data: PasswordChangeForm) => {
      try {
         setIsLoading(true);

         // Change password using the auth service, using tempPassword as currentPassword
         await authService.changePassword({
            currentPassword: tempPassword, // Use the stored temporary password
            newPassword: data.newPassword,
            confirmPassword: data.confirmPassword,
         });

         console.log('✅ Password changed successfully');
         toast.success('Password changed successfully! Welcome to WeTrippen Admin.');

         // Redirect to dashboard
         navigate('/');
      } catch (error: any) {
         const errorMessage =
            error.response?.data?.error || error.message || 'Failed to change password';
         toast.error(errorMessage);
      } finally {
         setIsLoading(false);
      }
   };

   const isInvitationLogin = !!invitationToken && !!invitationInfo;

  return (
     <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
           <div>
              <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
                 {isInvitationLogin ? 'Welcome to WeTrippen' : 'Admin Dashboard'}
              </h2>
              <p className='mt-2 text-center text-sm text-gray-600'>
                 {isInvitationLogin
                    ? 'Complete your account setup'
                    : 'Sign in to your admin account'}
              </p>
           </div>

           {/* Invitation Info Card */}
           {isInvitationLogin && (
              <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
                 <div className='flex items-center'>
                    <div className='flex-shrink-0'>
                       <svg
                          className='h-5 w-5 text-blue-400'
                          viewBox='0 0 20 20'
                          fill='currentColor'
                       >
                          <path
                             fillRule='evenodd'
                             d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                             clipRule='evenodd'
                          />
                       </svg>
                    </div>
                    <div className='ml-3'>
                       <h3 className='text-sm font-medium text-blue-800'>Admin Invitation</h3>
                       <div className='mt-2 text-sm text-blue-700'>
                          <p>
                             Welcome{' '}
                             <strong>
                                {invitationInfo.firstName} {invitationInfo.lastName}
                             </strong>
                          </p>
                          <p>
                             Role:{' '}
                             <span className='capitalize font-medium'>{invitationInfo.role}</span>
                          </p>
                          <p className='text-xs mt-1'>Use the temporary password from your email</p>
                       </div>
                    </div>
                 </div>
              </div>
           )}

           {isLoadingInvitation ? (
              <div className='text-center py-8'>
                 <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto'></div>
                 <p className='mt-2 text-sm text-gray-500'>Verifying invitation...</p>
              </div>
           ) : showPasswordChange ? (
              /* Password Change Form */
              <form
                 className='mt-8 space-y-6'
                 onSubmit={handlePasswordSubmit(onPasswordChangeSubmit)}
              >
                 <div className='bg-green-50 border border-green-200 rounded-lg p-4 mb-6'>
                    <div className='flex items-center'>
                       <div className='flex-shrink-0'>
                          <svg
                             className='h-5 w-5 text-green-400'
                             viewBox='0 0 20 20'
                             fill='currentColor'
                          >
                             <path
                                fillRule='evenodd'
                                d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                                clipRule='evenodd'
                             />
                          </svg>
                       </div>
                       <div className='ml-3'>
                          <h3 className='text-sm font-medium text-green-800'>
                             Invitation Verified
                          </h3>
                          <p className='text-sm text-green-700 mt-1'>
                             Welcome{' '}
                             <strong>
                                {loggedInUser?.firstName} {loggedInUser?.lastName}
                             </strong>
                             ! Please set your new password to complete your account setup.
                          </p>
                       </div>
                    </div>
                 </div>

                 <div className='space-y-4'>
                    {/* Current/Temporary Password (Pre-filled and Disabled) */}
                    <div>
                       <label
                          htmlFor='currentPassword'
                          className='block text-sm font-medium text-gray-700'
                       >
                          Current Password (Temporary)
                       </label>
                       <input
                          id='currentPassword'
                          name='currentPassword'
                          type='password'
                          value={tempPassword}
                          disabled
                          readOnly
                          className='mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-600 bg-gray-50 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm cursor-not-allowed'
                          placeholder='Your temporary password'
                       />
                       <p className='mt-1 text-xs text-gray-500'>
                          This is your temporary password from the invitation email
                       </p>
                    </div>

                    {/* New Password */}
                    <div>
                       <label
                          htmlFor='newPassword'
                          className='block text-sm font-medium text-gray-700'
                       >
                          New Password
                       </label>
                       <input
                          {...registerPassword('newPassword', {
                             required: 'New password is required',
                             minLength: {
                                value: 8,
                                message: 'Password must be at least 8 characters',
                             },
                          })}
                          id='newPassword'
                          name='newPassword'
                          type='password'
                          autoComplete='new-password'
                          className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                             passwordErrors.newPassword ? 'border-red-300' : 'border-gray-300'
                          } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                          placeholder='Enter your new password'
                       />
                       {passwordErrors.newPassword && (
                          <p className='mt-1 text-sm text-red-600'>
                             {passwordErrors.newPassword.message}
                          </p>
                       )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                       <label
                          htmlFor='confirmPassword'
                          className='block text-sm font-medium text-gray-700'
                       >
                          Confirm New Password
                       </label>
                       <input
                          {...registerPassword('confirmPassword', {
                             required: 'Please confirm your password',
                             validate: (value) => value === newPassword || 'Passwords do not match',
                          })}
                          id='confirmPassword'
                          name='confirmPassword'
                          type='password'
                          autoComplete='new-password'
                          className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                             passwordErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                          } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                          placeholder='Confirm your new password'
                       />
                       {passwordErrors.confirmPassword && (
                          <p className='mt-1 text-sm text-red-600'>
                             {passwordErrors.confirmPassword.message}
                          </p>
                       )}
                    </div>
                 </div>

                 <div>
                    <button
                       type='submit'
                       disabled={isLoading}
                       className='group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                       {isLoading ? (
                          <>
                             <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                             Setting up account...
                          </>
                       ) : (
                          'Complete Account Setup'
                       )}
                    </button>
                 </div>

                 <div className='text-center'>
                    <p className='text-xs text-gray-500'>
                       Your password must be at least 8 characters long
                    </p>
                 </div>
              </form>
           ) : (
              /* Login Form */
              <form className='mt-8 space-y-6' onSubmit={handleSubmit(onSubmit)}>
                 <div className='space-y-4'>
                    {/* Email Field */}
                    <div>
                       <label htmlFor='email' className='block text-sm font-medium text-gray-700'>
                          Email address
                       </label>
                       <input
                          {...register('email', {
                             required: 'Email is required',
                             pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Invalid email address',
                             },
                          })}
                          id='email'
                          name='email'
                          type='email'
                          autoComplete='email'
                          disabled={isInvitationLogin}
                          className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                             errors.email ? 'border-red-300' : 'border-gray-300'
                          } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm ${
                             isInvitationLogin ? 'bg-gray-50' : ''
                          }`}
                          placeholder='Enter your email address'
                       />
                       {errors.email && (
                          <p className='mt-1 text-sm text-red-600'>{errors.email.message}</p>
                       )}
                    </div>

                    {/* Password Field */}
                    <div>
                       <label
                          htmlFor='password'
                          className='block text-sm font-medium text-gray-700'
                       >
                          {isInvitationLogin ? 'Temporary Password' : 'Password'}
                       </label>
                       <input
                          {...register('password', {
                             required: 'Password is required',
                             minLength: isInvitationLogin
                                ? undefined
                                : { value: 8, message: 'Password must be at least 8 characters' },
                          })}
                          id='password'
                          name='password'
                          type='password'
                          autoComplete={isInvitationLogin ? 'new-password' : 'current-password'}
                          className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                             errors.password ? 'border-red-300' : 'border-gray-300'
                          } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                          placeholder={
                             isInvitationLogin
                                ? 'Enter temporary password from email'
                                : 'Enter your password'
                          }
                       />
                       {errors.password && (
                          <p className='mt-1 text-sm text-red-600'>{errors.password.message}</p>
                       )}
                       {isInvitationLogin && (
                          <p className='mt-1 text-xs text-gray-500'>
                             Check your email for the temporary password
                          </p>
                       )}
                    </div>
                 </div>

                 <div>
                    <button
                       type='submit'
                       disabled={isLoading}
                       className='group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                       {isLoading ? (
                          <>
                             <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                             {isInvitationLogin ? 'Setting up account...' : 'Signing in...'}
                          </>
                       ) : (
                          <>{isInvitationLogin ? 'Complete Setup' : 'Sign in'}</>
                       )}
                    </button>
                 </div>

                 {isInvitationLogin && (
                    <div className='text-center'>
                       <p className='text-xs text-gray-500'>
                          After signing in, you'll be asked to change your password
                       </p>
                    </div>
                 )}
              </form>
           )}
        </div>
     </div>
  );
};
